import React, { useState } from 'react';
import { Globe } from 'lucide-react';
import { SlamLoader } from '../../design-system/SlamLoader.jsx';
import { exportToPDF, exportToDOCX, exportToMarkdown } from '../../../lib/exportService';

const TOP_LANGUAGES = [
  { code: 'en', name: 'English', label: 'EN' },
  { code: 'es', name: 'Spanish', label: 'ES' },
  { code: 'fr', name: 'French', label: 'FR' },
  { code: 'de', name: 'German', label: 'DE' },
  { code: 'ja', name: 'Japanese', label: 'JA' },
  { code: 'ko', name: 'Korean', label: 'KO' },
  { code: 'zh', name: 'Chinese', label: 'ZH' },
  { code: 'pt', name: 'Portuguese', label: 'PT' },
];

const ALL_LANGUAGES = [
  ...TOP_LANGUAGES,
  { code: 'ar', name: 'Arabic', label: 'AR' },
  { code: 'it', name: 'Italian', label: 'IT' },
  { code: 'nl', name: 'Dutch', label: 'NL' },
  { code: 'ru', name: 'Russian', label: 'RU' },
  { code: 'tr', name: 'Turkish', label: 'TR' },
  { code: 'pl', name: 'Polish', label: 'PL' },
  { code: 'sv', name: 'Swedish', label: 'SV' },
  { code: 'hi', name: 'Hindi', label: 'HI' },
];

/**
 * ReportLanguageExport — One-click multilingual report generation
 * Appears below StructuredAnalysisReport when analysis is complete.
 *
 * Flow: user picks language → POST /api/video/translate-report →
 *       receives translated markdown → exports via exportService
 */
export function ReportLanguageExport({ reportContent, analysisMode = 'general', disabled = false }) {
  const [showAll, setShowAll] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translatingLang, setTranslatingLang] = useState(null);
  const [error, setError] = useState(null);
  const [exportFormat, setExportFormat] = useState('pdf');

  const languages = showAll ? ALL_LANGUAGES : TOP_LANGUAGES;

  const handleExport = async (lang) => {
    if (!reportContent || disabled) return;
    setError(null);

    if (lang.code === 'en') {
      // English: skip translation, export directly (show spinner during export)
      setIsTranslating(true);
      setTranslatingLang('en');
      try {
        if (exportFormat === 'pdf') await exportToPDF(reportContent, `slam-report-en`);
        else if (exportFormat === 'docx') await exportToDOCX(reportContent, `slam-report-en`);
        else await exportToMarkdown(reportContent, `slam-report-en`);
      } catch (err) {
        setError('Export failed: ' + err.message);
      } finally {
        setIsTranslating(false);
        setTranslatingLang(null);
      }
      return;
    }

    setIsTranslating(true);
    setTranslatingLang(lang.code);

    try {
      const res = await fetch('/api/video/translate-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportContent, targetLanguage: lang.code }),
      });

      if (!res.ok) throw new Error(`Translation failed (${res.status})`);
      const data = await res.json();
      // Backend uses respond() wrapper: { success, data: { translatedContent } }
      const translated = data?.data?.translatedContent || data.translatedContent || reportContent;

      if (exportFormat === 'pdf') await exportToPDF(translated, `slam-report-${lang.code}`);
      else if (exportFormat === 'docx') await exportToDOCX(translated, `slam-report-${lang.code}`);
      else await exportToMarkdown(translated, `slam-report-${lang.code}`);
    } catch (err) {
      setError(err.message || 'Translation failed');
    } finally {
      setIsTranslating(false);
      setTranslatingLang(null);
    }
  };

  if (!reportContent) return null;

  return (
    <div
      style={{
        marginTop: '1rem',
        padding: '0.875rem 1rem',
        background: 'var(--glass-background, rgba(255,255,255,0.04))',
        backdropFilter: 'blur(12px)',
        border: '1px solid var(--glass-border, rgba(255,255,255,0.08))',
        borderRadius: '0.75rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.625rem' }}>
        <Globe size={14} style={{ color: 'var(--slam-gold, #E8B800)', flexShrink: 0 }} />
        <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>
          Export report in:
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.25rem' }}>
          {['pdf', 'docx', 'md'].map(fmt => (
            <button
              key={fmt}
              onClick={() => setExportFormat(fmt)}
              style={{
                padding: '0.125rem 0.375rem',
                fontSize: '0.625rem',
                borderRadius: '0.25rem',
                border: '1px solid',
                borderColor: exportFormat === fmt ? 'var(--slam-gold, #E8B800)' : 'rgba(255,255,255,0.15)',
                background: exportFormat === fmt ? 'rgba(232,184,0,0.15)' : 'transparent',
                color: exportFormat === fmt ? 'var(--slam-gold, #E8B800)' : 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              {fmt}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', alignItems: 'center' }}>
        {languages.map(lang => {
          const active = isTranslating && translatingLang === lang.code;
          return (
            <button
              key={lang.code}
              onClick={() => handleExport(lang)}
              disabled={disabled || isTranslating}
              title={lang.name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                padding: '0.25rem 0.5rem',
                borderRadius: '0.375rem',
                border: '1px solid',
                borderColor: active ? 'var(--slam-gold, #E8B800)' : 'rgba(255,255,255,0.12)',
                background: active ? 'rgba(232,184,0,0.12)' : 'rgba(255,255,255,0.04)',
                color: active ? 'var(--slam-gold, #E8B800)' : 'rgba(255,255,255,0.7)',
                fontSize: '0.65rem',
                fontWeight: 600,
                cursor: disabled || isTranslating ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                transition: 'all 0.15s',
              }}
            >
              {active ? <SlamLoader size={10} /> : null}
              {lang.label}
            </button>
          );
        })}

        <button
          onClick={() => setShowAll(v => !v)}
          style={{
            padding: '0.25rem 0.5rem',
            borderRadius: '0.375rem',
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'transparent',
            color: 'rgba(255,255,255,0.35)',
            fontSize: '0.65rem',
            cursor: 'pointer',
          }}
        >
          {showAll ? 'Less' : 'More...'}
        </button>

        {isTranslating && (
          <span style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            <SlamLoader size={10} />
            Translating...
          </span>
        )}
      </div>

      {error && (
        <p style={{ marginTop: '0.5rem', fontSize: '0.7rem', color: 'var(--slam-gold, #E8B800)', opacity: 0.8 }}>
          {error}
        </p>
      )}
    </div>
  );
}

export default ReportLanguageExport;
